import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { settingsAPI, authAPI, uploadAPI, resolveImage } from '../utils/api';
import BilingualField from '../components/common/BilingualField';


const HK_BANKS = [
  'HSBC (The Hongkong and Shanghai Banking Corporation)',
  'Hang Seng Bank',
  'Bank of China (Hong Kong)',
  'Standard Chartered Bank (Hong Kong)',
  'The Bank of East Asia (BEA)',
  'Citibank (Hong Kong)',
  'DBS Bank (Hong Kong)',
  'Mox Bank',
  'ZA Bank',
  'Livi Bank',
  'WeLab Bank',
  'Other'
];

export default function AcademyLadder() {
  const { t } = useTranslation();
  const [levels, setLevels] = useState([]);
  const [feeDueDate, setFeeDueDate] = useState(5);
  const [makeupExpiryMonths, setMakeupExpiryMonths] = useState(2);
  const [monthlyMakeupLimit, setMonthlyMakeupLimit] = useState(2);
  const [dailyPromoPopupLimit, setDailyPromoPopupLimit] = useState(3);
  const [noticePeriodCutoffDay, setNoticePeriodCutoffDay] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inputLevel, setInputLevel] = useState('');
  const [academyName, setAcademyName] = useState({ en: '', zh: '' });
  const [activeTab, setActiveTab] = useState('profile');
  const [supportWhatsApp, setSupportWhatsApp] = useState('+852 5135 3966');
  const [supportEmail, setSupportEmail] = useState('info@ilyticstech.com');
  const [emailAppPassword, setEmailAppPassword] = useState('');
  const [showAppPassword, setShowAppPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestStatus, setEmailTestStatus] = useState(null);
  
  const [paymentDetails, setPaymentDetails] = useState({
    qrCodeUrl: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    fpsId: '',
    bankInstructions: { en: '', zh: '' }
  });
  const [qrFile, setQrFile] = useState(null);

  const [profileData, setProfileData] = useState({ name: '', nameZh: '', email: '', mobile: '', avatarUrl: '' });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await settingsAPI.get();
      setLevels(res.data.masterLevels || []);
      setAcademyName(typeof res.data.academyName === 'string' ? { en: res.data.academyName, zh: '' } : (res.data.academyName || { en: 'Academix', zh: '' }));
      setFeeDueDate(res.data.feeDueDate || 5);
      setMakeupExpiryMonths(res.data.makeupExpiryMonths ?? 2);
      setMonthlyMakeupLimit(res.data.monthlyMakeupLimit ?? 2);
      setDailyPromoPopupLimit(res.data.dailyPromoPopupLimit ?? 3);
      setNoticePeriodCutoffDay(res.data.noticePeriodCutoffDay ?? 15);
      if (res.data.supportWhatsApp) setSupportWhatsApp(res.data.supportWhatsApp);
      if (res.data.supportEmail) setSupportEmail(res.data.supportEmail);
      if (res.data.emailAppPassword) setEmailAppPassword(res.data.emailAppPassword);
      if (res.data.paymentDetails) {
        setPaymentDetails({
          ...res.data.paymentDetails,
          bankInstructions: typeof res.data.paymentDetails.bankInstructions === 'string' ? { en: res.data.paymentDetails.bankInstructions, zh: '' } : (res.data.paymentDetails.bankInstructions || { en: '', zh: '' })
        });
      }
      try {
        const meRes = await authAPI.getMe();
        if (meRes.data) {
          setProfileData({
            name: meRes.data.name || '',
            nameZh: meRes.data.nameZh || '',
            email: meRes.data.email || '',
            mobile: meRes.data.mobile || '',
            avatarUrl: meRes.data.avatarUrl || ''
          });
        }
      } catch (e) {
        console.error("Failed to load profile", e);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    const mobile = (profileData.mobile || '').trim();
    if (mobile) {
      if (mobile.startsWith('+852') || (mobile.startsWith('852') && mobile.length > 8)) {
        alert("Please enter only your 8-digit mobile number, without country code.");
        return;
      }
      if (!/^\d{8}$/.test(mobile)) {
        alert("Mobile number must be exactly 8 digits.");
        return;
      }
    }
    setSaving(true);
    try {
      let avatarUrl = profileData.avatarUrl;
      if (profilePhotoFile) {
        const formData = new FormData();
        formData.append('photo', profilePhotoFile);
        const upRes = await authAPI.uploadProfilePhoto(formData);
        if (upRes.data && upRes.data.url) avatarUrl = upRes.data.url;
      }
      const res = await authAPI.updateProfile({ ...profileData, mobile, avatarUrl });
      if (res.data?.user) {
        setProfileData({
          name: res.data.user.name || '',
          nameZh: res.data.user.nameZh || '',
          email: res.data.user.email || '',
          mobile: res.data.user.mobile || '',
          avatarUrl: res.data.user.avatarUrl || ''
        });
      }
      alert("Admin profile updated successfully!");
      setProfilePhotoFile(null);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      alert("Please enter current and new password.");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert("New password must be at least 6 characters.");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New password and confirm password do not match.");
      return;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      alert("New password must be different from current password.");
      return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      alert("Password updated successfully!");
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (newLevels) => {
    setSaving(true);
    try {
      await settingsAPI.update({ masterLevels: newLevels, academyName });
      setLevels(newLevels);
    } catch (err) {
      alert('Failed to update levels');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDueDate = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({
        feeDueDate: Number(feeDueDate),
        makeupExpiryMonths: Number(makeupExpiryMonths),
        monthlyMakeupLimit: Number(monthlyMakeupLimit),
        dailyPromoPopupLimit: Number(dailyPromoPopupLimit),
        noticePeriodCutoffDay: Number(noticePeriodCutoffDay),
        academyName
      });
      alert('Configurations updated successfully!');
    } catch (err) {
      alert('Failed to update finance settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePaymentDetails = async () => {
    setSaving(true);
    try {
      let qrCodeUrl = paymentDetails.qrCodeUrl;
      if (qrFile) {
        const formData = new FormData();
        formData.append('photo', qrFile);
        const uploadRes = await uploadAPI.qrCode(formData);
        qrCodeUrl = uploadRes.data.url;
      }
      const updatedDetails = { ...paymentDetails, qrCodeUrl };
      await settingsAPI.update({ paymentDetails: updatedDetails, academyName });
      setPaymentDetails(updatedDetails);
      setQrFile(null);
      alert('Payment details updated successfully');
    } catch (err) {
      alert('Failed to update payment details');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSupport = async () => {
    setSaving(true);
    setEmailTestStatus(null);
    try {
      await settingsAPI.update({
        supportWhatsApp: supportWhatsApp.trim(),
        supportEmail: supportEmail.trim(),
        emailAppPassword: emailAppPassword.trim(),
        academyName
      });
      alert('Support & Email configurations updated successfully!');
    } catch (err) {
      alert('Failed to update support settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setEmailTestStatus(null);
    try {
      const res = await settingsAPI.testEmail();
      setEmailTestStatus({ success: true, message: res.data.message || 'Test email sent successfully!' });
    } catch (err) {
      setEmailTestStatus({ 
        success: false, 
        message: err.response?.data?.message || err.message || 'Failed to send test email. Check email and password.' 
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const addLevel = () => {
    if (!inputLevel.trim()) return;
    const newList = [...levels, inputLevel.trim()];
    handleUpdate(newList);
    setInputLevel('');
  };

  const removeLevel = (idx) => {
    const newList = levels.filter((_, i) => i !== idx);
    handleUpdate(newList);
  };

  const moveLevel = (idx, direction) => {
    const newList = [...levels];
    const target = idx + direction;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    handleUpdate(newList);
  };

  return (
    <Layout title={t('settings.title', 'Global Settings')}>
      <div className="dash-page-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="dash-page-title">{t('settings.title', 'Global Settings')}</h1>
          <p className="text-sm text-muted">{t('settings.subtitle', 'Manage levels, finance configurations, and global academy settings.')}</p>
        </div>
      </div>

      <div style={{ display:'flex', gap:20, borderBottom:'1px solid var(--border)', marginBottom:24 }}>
        {[
          { id: 'profile', label: t('settings.tabProfile', 'Admin Profile') },
          { id: 'ladder', label: t('settings.tabLadder', 'Academy Ladder') },
          { id: 'finance', label: t('settings.tabFinance', 'Finance Configuration') },
          { id: 'payment', label: t('settings.tabPayment', 'Payment Details') },
          { id: 'support', label: t('settings.tabSupport', 'Support & Recovery') }
        ].map(tab => (
          <div 
            key={tab.id} 
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-light)'
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="dash-loading">⏳ {t('settings.loading', 'Loading settings...')}</div>
      ) : (
        <div>
          {activeTab === 'profile' && (
            <div className="grid-2">
              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">{t('settings.profileDetails', 'Profile Details')}</div>
                </div>
                <div className="mb-4">
                  <BilingualField 
                    label={t('settings.fullName', 'Full Name')}
                    value={{ en: profileData.name, zh: profileData.nameZh }}
                    onChange={(_, lang, val) => {
                      if (lang === 'en') setProfileData({ ...profileData, name: val });
                      if (lang === 'zh') setProfileData({ ...profileData, nameZh: val });
                    }}
                  />
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.email', 'Email Address')}</label>
                  <input 
                    type="email" 
                    className="form-input-style" 
                    value={profileData.email} 
                    onChange={e => setProfileData({ ...profileData, email: e.target.value })} 
                  />
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.mobile', 'Mobile Number')}</label>
                  <input 
                    type="text" 
                    maxLength={8}
                    placeholder={t('settings.mobilePlaceholder', '8-digit HK mobile number')}
                    className="form-input-style" 
                    value={profileData.mobile} 
                    onChange={e => setProfileData({ ...profileData, mobile: e.target.value.replace(/\D/g, '') })} 
                  />
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.profilePhoto', 'Profile Photo')}</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="form-input-style" 
                    style={{ padding: '8px' }} 
                    onChange={e => setProfilePhotoFile(e.target.files[0])} 
                  />
                  {profilePhotoFile && <div className="text-xs text-success mt-2">✅ Selected: {profilePhotoFile.name}</div>}
                  {!profilePhotoFile && profileData.avatarUrl && (
                    <div className="mt-3">
                      <img src={resolveImage(profileData.avatarUrl)} alt="Avatar" style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }} />
                    </div>
                  )}
                </div>
                <button className="btn btn-primary mt-2" onClick={handleSaveProfile} disabled={saving}>{t('settings.updateProfile', 'Update Profile')}</button>
              </div>

              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">{t('settings.securityPassword', 'Security & Password')}</div>
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.currentPassword', 'Current Password')}</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showCurrentPassword ? "text" : "password"} 
                      className="form-input-style" 
                      style={{ paddingRight: '40px' }}
                      value={passwordData.currentPassword} 
                      onChange={e => setPasswordData({ ...passwordData, currentPassword: e.target.value })} 
                    />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                      {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.newPassword', 'New Password')}</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showNewPassword ? "text" : "password"} 
                      className="form-input-style" 
                      style={{ paddingRight: '40px' }}
                      value={passwordData.newPassword} 
                      onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} 
                    />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                      {showNewPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.confirmPassword', 'Confirm New Password')}</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showConfirmPassword ? "text" : "password"} 
                      className="form-input-style" 
                      style={{ paddingRight: '40px' }}
                      value={passwordData.confirmPassword} 
                      onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} 
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                <button className="btn btn-primary mt-2" onClick={handleSavePassword} disabled={saving}>{t('settings.resetPassword', 'Reset Password')}</button>
              </div>
            </div>
          )}

          {activeTab === 'ladder' && (
            <div className="grid-2">
              <div className="dash-card">
            <div className="dash-card-header">
              <div className="dash-card-title">{t('settings.academyConfig', 'Academy Configuration')}</div>
            <div style={{ marginBottom: 30 }}>
              <BilingualField label="Academy Name" value={academyName} onChange={(_, lang, val) => setAcademyName(p => ({ ...p, [lang]: val }))} placeholder="e.g. ETS Academy" />
            </div>
            <div className="dash-card-title mt-4">{t('settings.defineSteps', 'Define Growth Steps')}</div>
              {saving && <span className="text-xs text-muted">{t('settings.saving', 'Saving...')}</span>}
            </div>
            
            <div className="form-group" style={{ display:'flex', gap:10 }}>
              <input 
                type="text" 
                className="form-input-style" 
                placeholder="e.g., Advanced Plus"
                value={inputLevel}
                onChange={e => setInputLevel(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && addLevel()}
              />
              <button className="btn btn-primary" onClick={addLevel} disabled={saving}>{t('settings.addLevel', 'Add')}</button>
            </div>

            <div className="dash-list" style={{ marginTop:24 }}>
              {levels.map((l, i) => (
                <div key={i} className="dash-list-item" style={{ cursor:'default' }}>
                  <div className="dash-avatar" style={{ width:28, height:28, fontSize:12, background:'var(--black)', color:'white', border:'none' }}>
                    {i + 1}
                  </div>
                  <div className="dash-list-item-main">
                    <div className="dash-list-item-name">{l}</div>
                    <div className="text-xs text-muted">Step {i + 1} in growth path</div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => moveLevel(i, -1)} disabled={i === 0 || saving}>↑</button>
                    <button className="btn btn-sm btn-outline" onClick={() => moveLevel(i, 1)} disabled={i === levels.length - 1 || saving}>↓</button>
                    <button className="btn btn-sm btn-danger" onClick={() => removeLevel(i)} disabled={saving} style={{ padding:'8px 12px' }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card" style={{ background:'var(--black)', color:'white' }}>
            <div style={{ fontSize:32, marginBottom:16 }}>🪜</div>
            <h3 className="dash-modal-title" style={{ color:'white', marginBottom:12 }}>{t('settings.ladderTitle', 'The Academic Ladder')}</h3>
            <p className="text-sm mb-4" style={{ color:'rgba(255,255,255,.5)' }}>
              {t('settings.ladderDesc', 'Reordering these levels will immediately update the "Promotions" tool. Levels should generally follow an increase in difficulty or frequency of training.')}
            </p>
            <div style={{ padding:20, background:'rgba(255,255,255,.05)', border:'1px dashed rgba(255,255,255,.1)' }}>
              <div className="text-xs text-muted mb-4">{t('settings.studentPreview', 'STUDENT PREVIEW')}</div>
              {levels.map((l, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: i === 0 ? 'var(--wine-light)' : 'rgba(255,255,255,.15)' }}></div>
                  <div className="text-sm" style={{ opacity: i === 0 ? 1 : .4 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          </div>
          )}

          {activeTab === 'finance' && (
          <div className="dash-card" style={{ maxWidth: 600 }}>
            <div className="dash-card-header">
              <div className="dash-card-title">{t('settings.tabFinance', 'Finance & Attendance Configuration')}</div>
            </div>
            
            <div className="form-group mb-6">
              <label>{t('settings.defaultFeeDate', 'Default Fee Due Date')}</label>
              <p className="text-xs text-muted mb-4" style={{ marginTop: 4 }}>
                {t('settings.defaultFeeDateDesc', 'Students will be marked as overdue if payment is not received by this day of the month.')}
              </p>
              <div style={{ display:'flex', gap:10, alignItems: 'center' }}>
                <input 
                  type="number" 
                  min="1"
                  max="28"
                  className="form-input-style" 
                  value={feeDueDate}
                  onChange={e => setFeeDueDate(e.target.value)}
                  style={{ width: '120px' }}
                />
                <span className="text-sm text-muted">th day of each month</span>
              </div>
            </div>

            <div className="form-group mb-6" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <label style={{ fontWeight: 'bold' }}>Make-up Class Expiry / Deadline</label>
              <p className="text-xs text-muted mb-4" style={{ marginTop: 4 }}>
                Number of months after an approved leave within which the student must complete their make-up class before it expires.
              </p>
              <div style={{ display:'flex', gap:10, alignItems: 'center' }}>
                <input 
                  type="number" 
                  min="1"
                  max="12"
                  className="form-input-style" 
                  value={makeupExpiryMonths}
                  onChange={e => setMakeupExpiryMonths(e.target.value)}
                  style={{ width: '120px' }}
                />
                <span className="text-sm text-muted">month(s) expiry deadline</span>
              </div>
            </div>

            <div className="form-group mb-6" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <label style={{ fontWeight: 'bold' }}>Monthly Make-up Class Request Limit</label>
              <p className="text-xs text-muted mb-4" style={{ marginTop: 4 }}>
                Maximum number of make-up class requests or allocations allowed per student within a single calendar month (e.g. 2 per month).
              </p>
              <div style={{ display:'flex', gap:10, alignItems: 'center' }}>
                <input 
                  type="number" 
                  min="0"
                  max="10"
                  className="form-input-style" 
                  value={monthlyMakeupLimit}
                  onChange={e => setMonthlyMakeupLimit(e.target.value)}
                  style={{ width: '120px' }}
                />
                <span className="text-sm text-muted">session(s) per student / month</span>
              </div>
            </div>

            <div className="form-group mb-6" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <label style={{ fontWeight: 'bold' }}>Daily Promotion Pop-up Limit</label>
              <p className="text-xs text-muted mb-4" style={{ marginTop: 4 }}>
                Maximum number of times the batch promotion popup dialog will appear per user per day when opening the mobile app.
              </p>
              <div style={{ display:'flex', gap:10, alignItems: 'center' }}>
                <input 
                  type="number" 
                  min="1"
                  max="20"
                  className="form-input-style" 
                  value={dailyPromoPopupLimit}
                  onChange={e => setDailyPromoPopupLimit(e.target.value)}
                  style={{ width: '120px' }}
                />
                <span className="text-sm text-muted">time(s) per day</span>
              </div>
            </div>

            <div className="form-group mb-6" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <label style={{ fontWeight: 'bold' }}>Notice Period Cutoff Day / 退學通知期截止日</label>
              <p className="text-xs text-muted mb-4" style={{ marginTop: 4 }}>
                Day of the month by which parents must submit withdrawal notices for discontinuation in the subsequent month (e.g., 15th).
              </p>
              <div style={{ display:'flex', gap:10, alignItems: 'center' }}>
                <input 
                  type="number" 
                  min="1"
                  max="28"
                  className="form-input-style" 
                  value={noticePeriodCutoffDay}
                  onChange={e => setNoticePeriodCutoffDay(e.target.value)}
                  style={{ width: '120px' }}
                />
                <span className="text-sm text-muted">th day of each month</span>
              </div>
            </div>

            <button className="btn btn-primary mt-4" onClick={handleUpdateDueDate} disabled={saving}>{t('settings.saveConfig', 'Save Configurations')}</button>
          </div>
          )}

          {activeTab === 'payment' && (
          <div className="payment-details-container">
            <p className="text-sm text-muted mb-4">
              {t('settings.paymentDesc', "Configure your bank details and upload your organization's payment QR code. These will be securely displayed to parents when they pay fees via Bank/QR Transfer.")}
            </p>
            <div className="grid-2">
              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">{t('settings.bankDetails', 'Bank Transfer Details')}</div>
                </div>
                
                <div className="form-group mb-4">
                  <label>{t('settings.bankName', 'Bank Name')}</label>
                  <select 
                    className="form-input-style" 
                    value={paymentDetails.bankName}
                    onChange={e => setPaymentDetails(p => ({...p, bankName: e.target.value}))}
                  >
                    <option value="">{t('settings.selectBank', 'Select a Bank...')}</option>
                    {HK_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                <div className="form-group mb-4">
                  <label>{t('settings.accountName', 'Account Name')}</label>
                  <input 
                    type="text" 
                    className="form-input-style" 
                    placeholder="e.g. ETS Academy Ltd."
                    value={paymentDetails.accountName}
                    onChange={e => setPaymentDetails(p => ({...p, accountName: e.target.value}))}
                  />
                </div>

                <div className="form-group mb-4">
                  <label>{t('settings.accountNumber', 'Account Number')}</label>
                  <input 
                    type="text" 
                    className="form-input-style" 
                    placeholder="e.g. 123-456789-001"
                    value={paymentDetails.accountNumber}
                    onChange={e => setPaymentDetails(p => ({...p, accountNumber: e.target.value}))}
                  />
                </div>

                <div className="form-group mb-4">
                  <BilingualField label="Bank Transfer Instructions" value={paymentDetails.bankInstructions} onChange={(_, lang, val) => setPaymentDetails(p => ({ ...p, bankInstructions: { ...p.bankInstructions, [lang]: val } }))} isTextarea rows={3} placeholder="e.g. Please include your student ID in the transfer remarks." />
                </div>
                <div className="form-group mb-4">
                  <label>{t('settings.fpsId', 'FPS ID / Phone Number (Optional)')}</label>
                  <input 
                    type="text" 
                    className="form-input-style" 
                    placeholder="e.g. 12345678"
                    value={paymentDetails.fpsId}
                    onChange={e => setPaymentDetails(p => ({...p, fpsId: e.target.value}))}
                  />
                </div>
              </div>

              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">{t('settings.paymentQR', 'Payment QR Code')}</div>
                </div>

                <div className="form-group mb-4">
                  <label>{t('settings.qrImage', 'QR Code Image')}</label>
                  <input 
                    type="file" 
                    accept="image/*"
                    className="form-input-style" 
                    style={{ padding: '8px' }}
                    onChange={e => setQrFile(e.target.files[0])}
                  />
                  {qrFile && <div className="text-xs text-success mt-2">✅ New QR selected: {qrFile.name}</div>}
                  {!qrFile && paymentDetails.qrCodeUrl && (
                    <div className="mt-4">
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>{t('settings.currentQR', 'CURRENT QR CODE')}</div>
                      <img 
                        src={resolveImage(paymentDetails.qrCodeUrl)} 
                        alt="Current QR Code" 
                        style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid #eaeaea', borderRadius: '8px', padding: '4px', background: '#fff' }} 
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <button className="btn btn-primary" onClick={handleUpdatePaymentDetails} disabled={saving}>{t('settings.savePayment', 'Save Payment Details')}</button>
            </div>
          </div>
          )}

          {activeTab === 'support' && (
            <div>
              <div className="grid-2">
                <div className="dash-card">
                  <div className="dash-card-header">
                    <div className="dash-card-title">💬 WhatsApp Customer Support</div>
                  </div>
                  <p className="text-sm text-muted mb-4">
                    This phone number is displayed across the ETS Mobile App (login screen, "Forgot Password?" dialog, and parent dashboard) so students and parents can contact academy administration directly via WhatsApp.
                  </p>
                  <div className="form-group mb-4">
                    <label>WhatsApp Number (with country code)</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input 
                        type="text" 
                        className="form-input-style" 
                        placeholder="e.g. +852 5135 3966"
                        value={supportWhatsApp}
                        onChange={e => setSupportWhatsApp(e.target.value)}
                      />
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          const cleanNum = supportWhatsApp.replace(/\D/g, '');
                          if (cleanNum) {
                            window.open(`https://wa.me/${cleanNum}?text=Hello%20ETS%20Academy%20Support`, '_blank');
                          } else {
                            alert('Please enter a valid phone number first');
                          }
                        }}
                      >
                        Test Chat
                      </button>
                    </div>
                    <span style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'block' }}>
                      Tip: Include international prefix (e.g. +852 5135 3966 or 85251353966).
                    </span>
                  </div>
                </div>

                <div className="dash-card">
                  <div className="dash-card-header">
                    <div className="dash-card-title">✉️ Support & Automated Email Dispatch</div>
                  </div>
                  <p className="text-sm text-muted mb-4">
                    Sender email and Google credentials used by ETS Academy to automatically dispatch password reset links to students, parents, and tutors.
                  </p>
                  
                  <div className="form-group mb-4">
                    <label>Sender & Support Email Address</label>
                    <input 
                      type="email" 
                      className="form-input-style" 
                      placeholder="e.g. ucemdheeraj006@gmail.com"
                      value={supportEmail}
                      onChange={e => setSupportEmail(e.target.value)}
                    />
                    <span style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'block' }}>
                      All password reset emails will be dispatched from this email address.
                    </span>
                  </div>

                  <div className="form-group mb-4">
                    <label>Gmail App Password (16-letter code)</label>
                    <div style={{ position: 'relative' }}>
                      <input 
                        type={showAppPassword ? "text" : "password"} 
                        className="form-input-style" 
                        placeholder="e.g. abcd efgh ijkl mnop"
                        style={{ paddingRight: '40px', letterSpacing: showAppPassword ? '1px' : 'normal', fontFamily: 'monospace' }}
                        value={emailAppPassword}
                        onChange={e => setEmailAppPassword(e.target.value)}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowAppPassword(!showAppPassword)} 
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                      >
                        {showAppPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    <span style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'block' }}>
                      Generated from Google Account &gt; Security &gt; App passwords. Allows the website to send reset emails.
                    </span>
                  </div>

                  {emailTestStatus && (
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      marginBottom: '16px',
                      background: emailTestStatus.success ? '#e8f5e9' : '#ffebee',
                      color: emailTestStatus.success ? '#2e7d32' : '#c62828',
                      border: `1px solid ${emailTestStatus.success ? '#a5d6a7' : '#ef9a9a'}`
                    }}>
                      {emailTestStatus.success ? '✅ ' : '❌ '}
                      {emailTestStatus.message}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handleTestEmail}
                      disabled={testingEmail || !supportEmail || !emailAppPassword}
                      style={{ fontSize: '13px' }}
                    >
                      {testingEmail ? 'Testing Connection...' : '🧪 Test Email Connection'}
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 24 }}>
                <button className="btn btn-primary" onClick={handleUpdateSupport} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Support & Email Configurations'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
